// utils/accessUtil.js

function getAllMatchingRules(roles, matrix) {
    // Filter out default/system roles to prioritize explicit app groups
    const filtered = roles.filter(r =>
        r && !/^default-roles/i.test(r) && !/offline_access|uma_authorization/i.test(r)
    );
    
    const matchedRules = [];
    
    // First, collect all matching rules from filtered roles
    for (const r of filtered) {
        if (matrix[r]) {
            matchedRules.push({ ...matrix[r], matchedRole: r });
        }
    }
    
    // If no matches in filtered roles, check all roles
    if (matchedRules.length === 0) {
        for (const r of roles) {
            if (matrix[r]) {
                matchedRules.push({ ...matrix[r], matchedRole: r });
            }
        }
    }
    
    return matchedRules;
}

function mergeRules(rules) {
    if (rules.length === 0) return {};
    if (rules.length === 1) return rules[0];
    
    // Check if any rule has redirect - highest priority
    const redirectRule = rules.find(r => r.redirect);
    if (redirectRule) return redirectRule;
    
    const merged = {
        sidebar: [],
        sidebarDeny: [],
        features: [],
        matchedRole: rules.map(r => r.matchedRole).join(', ')
    };
    
    let hasWildcardSidebar = false;
    let hasWildcardFeatures = false;
    
    for (const rule of rules) {
        // Merge sidebar permissions
        if (rule.sidebar === "*") {
            hasWildcardSidebar = true;
        } else if (Array.isArray(rule.sidebar)) {
            merged.sidebar.push(...rule.sidebar);
        }
        
        // Merge sidebarDeny
        if (Array.isArray(rule.sidebarDeny)) {
            merged.sidebarDeny.push(...rule.sidebarDeny);
        }
        
        // Merge features
        if (rule.features === "*") {
            hasWildcardFeatures = true;
        } else if (Array.isArray(rule.features)) {
            merged.features.push(...rule.features);
        }
    }
    
    // Set wildcards if any rule has them
    if (hasWildcardSidebar) merged.sidebar = "*";
    else merged.sidebar = [...new Set(merged.sidebar)]; // Remove duplicates
    
    if (hasWildcardFeatures) merged.features = "*";
    else merged.features = [...new Set(merged.features)]; // Remove duplicates
    
    // Remove duplicates from sidebarDeny
    merged.sidebarDeny = [...new Set(merged.sidebarDeny)];
    
    return merged;
}

function filterTree(menu, allowedKeys = "*", denyKeys = []) {
    return menu
        .filter(item => {
            if (denyKeys.includes(item.key)) return false;
            if (allowedKeys === "*") return true;
            if (!allowedKeys.includes(item.key)) return false;
            return true;
        })
        .map(item => {
            if (item.children) {
                const filteredChildren = filterTree(item.children, allowedKeys, denyKeys);
                return { ...item, children: filteredChildren };
            }
            return item;
        })
        .filter(item => !item.children || item.children.length > 0);
}

export function getSidebarForRoles(roles, menu, matrix) {
    const matchedRules = getAllMatchingRules(roles, matrix);
    const rule = mergeRules(matchedRules);
    
    if (rule.redirect) return { type: "redirect", url: rule.redirect };
    if (rule.sidebar === "*") {
        let filtered = menu;
        if (rule.sidebarDeny && rule.sidebarDeny.length > 0) {
            filtered = filterTree(menu, "*", rule.sidebarDeny);
        }
        return filtered;
    }
    return filterTree(menu, rule.sidebar || []);
}

export function getFeaturesForRoles(roles, registry, matrix) {
    const matchedRules = getAllMatchingRules(roles, matrix);
    const rule = mergeRules(matchedRules);
    
    if (rule.features === "*") return registry;
    if (Array.isArray(rule.features)) {
        return registry.filter(f => rule.features.includes(f.key));
    }
    return [];
}

export function canAccessSidebarKey(roles, key, matrix) {
    const matchedRules = getAllMatchingRules(roles, matrix);
    const rule = mergeRules(matchedRules);
    if (!rule || rule.redirect) return false;
    if (rule.sidebar === "*") {
        return !(rule.sidebarDeny || []).includes(key);
    }
    return Array.isArray(rule.sidebar) && rule.sidebar.includes(key);
}
